import * as React from 'react';
import { toastr } from 'react-redux-toastr';
import { useDataLoader } from 'react-remote-data-hooks';
import { useStateWithCallbackLazy } from 'use-state-with-callback';
import api from '../../../../api';
import CreateForm from './CreateForm';
import { useHasPermission } from '../../../../core/hooks';
import { AppPermissions, District, IGovernorate } from '../../../../core';
import { StatusCodes } from 'http-status-codes';

const Districts: React.FunctionComponent = () => {
  const canDeleteDistrict = useHasPermission(AppPermissions.DeleteDistrict);
  const canCreateDistrict = useHasPermission(AppPermissions.CreateDistrict);
  const canUpdateDistrict = useHasPermission(AppPermissions.UpdateDistrict);
  const [editModal, setEditModal] = useStateWithCallbackLazy<{
    data?: District;
    isOpen?: boolean;
  }>({});
  const { data, reload } = useDataLoader<District[]>(() =>
    api.district.loadAllDistricts()
  );
  const [updateError, setUpdateError] = React.useState('');
  const [updating, setUpdating] = React.useState(Boolean);

  const { data: governorates } = useDataLoader<IGovernorate[]>(() =>
    api.governorate.loadAllGovernorates()
  );

  const districtUpdate = async (updateData: District) => {
    if (updateData.name.trim() === '') {
      toastr.error('Error', 'District name cannot be empty spaces!');
    } else {
      try {
        const id = editModal?.data?._id;
        let isDistrictExist: boolean = false;
        data.map(
          (district) =>
            district.name?.trim() === updateData?.name?.trim() &&
            district.governorate === updateData?.governorate &&
            !id &&
            (isDistrictExist = true)
        );
        if (isDistrictExist) {
          toastr.error('Error', 'District already exists');
        } else {
          setUpdating(true);
          const response = id
            ? await api.district.updateDistrict(
                updateData,
                editModal?.data?._id
              )
            : await api.district.createDistrict(updateData);
          if (response.status === StatusCodes.OK) {
            setEditModal({}, () => {
              reload();
            });
          } else {
            setUpdateError('Unable to update details');
          }
          setUpdating(false);
        }
      } catch (error) {
        setUpdateError('Unable to update details');
        setUpdating(false);
      }
    }
  };

  const onDelete = async (id?: string) => {
    try {
      await api.district.deleteDistrict(id);
      toastr.success('Success', 'District deleted successfully');
      reload();
      closeModal();
    } catch (error) {
      toastr.error('Error', error?.response?.data?.message);
    }
  };

  const closeModal = () => {
    setEditModal({}, () => {
      // console.log("model closed")
    });
  };

  const showEditModal = (modeData: District) => {
    setEditModal({}, () => {
      setEditModal({ data: modeData, isOpen: true }, () => {
        // console.log("model closed")
      });
    });
  };
  const showCreateModal = () => {
    setEditModal({}, () => {
      setEditModal({ isOpen: true }, () => {
        // console.log("model closed")
      });
    });
  };

  return (
    <>
      <div className="col-lg-4 col-md-6 col-12 border-right">
        <div className="d-flex align-items-center py-3">
          <div className="col">
            <h4 className="mb-0 font-weight-semibold">Districts</h4>
          </div>
          {canCreateDistrict && (
            <div className="col-auto text-right">
              <button
                className="btn btn-link font-normal font-weight-semibold px-2 py-2 d-inline-flex align-items-center"
                onClick={() => showCreateModal()}
              >
                <i className="icon-plus mr-1 font-xs"></i> Add
              </button>
            </div>
          )}
        </div>
        <div className="grid-table grid-table--sm border-top">
          <table>
            <thead>
              <tr>
                <th>
                  <span className="d-inline-flex align-items-center">Name</span>
                </th>
                <th>
                  <span className="d-inline-flex align-items-center">
                    Governorate
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.map((item) => {
                return (
                  <tr
                    onClick={() => {
                      if (canDeleteDistrict || canUpdateDistrict) {
                        showEditModal(item);
                      }
                    }}
                    className="cursor-pointer"
                  >
                    <td>{item.name}</td>
                    <td>{item.governorate}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="col-lg col-md-6 col-12 p-2 setting-detail-block left-0 top-0 bg-white w-100 h-100 zIndex-1">
        {editModal.isOpen && (
          <>
            <div className="text-right d-md-none">
              <i className="icon-close"></i>
            </div>
            <CreateForm
              onSubmit={(formData) => districtUpdate(formData)}
              isSubmitting={updating}
              error={updateError}
              data={editModal.data}
              onCancel={() => closeModal()}
              onDelete={(id?: string) => onDelete(id)}
              sourceList={governorates}
            ></CreateForm>
          </>
        )}
      </div>
    </>
  );
};

export default Districts;
